import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";

export interface LogEntry {
  time: string;
  message: string;
  level?: "error" | "info" | "warning";
}

interface LogsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logs: LogEntry[];
  isLoading: boolean;
  title?: string;
}

export function LogsDialog({
  open,
  onOpenChange,
  logs,
  isLoading,
  title = "Logs",
}: LogsDialogProps) {
  const scrollAreaRef = useRef<React.ElementRef<typeof ScrollArea>>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const previousLogsLengthRef = useRef(0);

  // Helper function to get the viewport element
  const getViewport = (): HTMLDivElement | null => {
    if (!scrollAreaRef.current) return null;
    // The ScrollArea ref points to the Root element, find the viewport inside it
    const root = scrollAreaRef.current as unknown as HTMLElement;
    // Radix UI ScrollArea viewport is typically the first div child with overflow styles
    // Try multiple selectors to find it reliably
    let viewport = root.querySelector('[data-radix-scroll-area-viewport]') as HTMLDivElement;
    if (viewport) return viewport;
    
    // Fallback: find by structure - viewport is usually the first direct child div
    const firstDiv = root.querySelector('div > div') as HTMLDivElement;
    if (firstDiv && firstDiv.scrollHeight !== undefined) {
      return firstDiv;
    }
    
    // Last resort: find any scrollable div
    const divs = root.querySelectorAll('div');
    for (const div of Array.from(divs)) {
      if (div.scrollHeight > div.clientHeight) {
        return div as HTMLDivElement;
      }
    }
    
    return null;
  };

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (!open) {
      return;
    }

    const viewport = getViewport();
    if (!viewport) {
      return;
    }

    // Check if user is near the bottom (within 50px)
    const isNearBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 50;

    // Only auto-scroll if:
    // 1. User is already near the bottom (shouldAutoScroll is true), OR
    // 2. This is the first time logs are being added and user hasn't scrolled
    if (shouldAutoScroll || (logs.length > previousLogsLengthRef.current && isNearBottom)) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        const vp = getViewport();
        if (vp) {
          vp.scrollTop = vp.scrollHeight;
        }
      });
      setShouldAutoScroll(true);
    }

    previousLogsLengthRef.current = logs.length;
  }, [logs, open, shouldAutoScroll]);

  // Handle manual scrolling - pause auto-scroll if user scrolls up
  useEffect(() => {
    if (!open) return;

    const viewport = getViewport();
    if (!viewport) return;

    const handleScroll = () => {
      const vp = getViewport();
      if (!vp) return;
      const isAtBottom =
        vp.scrollHeight - vp.scrollTop - vp.clientHeight < 10;
      setShouldAutoScroll(isAtBottom);
    };

    viewport.addEventListener('scroll', handleScroll);
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [open]);

  // Reset auto-scroll when dialog opens
  useEffect(() => {
    if (open) {
      setShouldAutoScroll(true);
      previousLogsLengthRef.current = 0;
      // Scroll to bottom when dialog opens
      setTimeout(() => {
        const viewport = getViewport();
        if (viewport) {
          viewport.scrollTop = viewport.scrollHeight;
        }
      }, 100);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <ScrollArea ref={scrollAreaRef} className="h-[60vh] rounded-md border p-4">
          <div ref={logsContainerRef} className="space-y-1 font-mono text-sm">
            {logs.map((log, idx) => (
              <div key={idx} className={log.level === "error" ? "text-red-600" : ""}>
                [{log.time}] {log.message}
              </div>
            ))}
            {isLoading && (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Processing...</span>
              </div>
            )}
            {logs.length === 0 && !isLoading && (
              <div className="text-muted-foreground text-center py-4">
                No logs yet
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

